import { useState, useEffect } from "react";
import { Nav } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import {
  FaBox,
  FaChartBar,
  FaUsers,
  FaClipboardList,
  FaTags,
  FaBell,
  FaCogs,
  FaWarehouse,
  FaPalette,
  FaRuler,
  FaThList,
} from "react-icons/fa";

const links = [
  { to: "/", label: "Dashboard", icon: <FaChartBar /> },
  { to: "/products", label: "Products", icon: <FaBox /> },
  { to: "/orders", label: "Orders", icon: <FaClipboardList /> },
  { to: "/customers", label: "Customers", icon: <FaUsers /> },
  { to: "/inventory", label: "Inventory", icon: <FaWarehouse /> },
  { to: "/categories", label: "Categories", icon: <FaThList /> },
  { to: "/colors", label: "Colors", icon: <FaPalette /> },
  { to: "/sizes", label: "Sizes", icon: <FaRuler /> },
  { to: "/coupons", label: "Coupons", icon: <FaTags /> },
  { to: "/reviews", label: "Reviews", icon: <FaClipboardList /> },
  { to: "/notifications", label: "Notifications", icon: <FaBell /> },
  { to: "/settings", label: "Settings", icon: <FaCogs /> },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleLinkClick = () => {
    if (isMobile) {
      onClose();
    }
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && isMobile && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1040,
            transition: "opacity 0.3s ease-in-out",
          }}
        />
      )}

      {/* Sidebar */}
      <div
        className={`sidebar-wrapper bg-dark text-white ${
          isOpen ? "sidebar-open" : "sidebar-closed"
        }`}
        style={{
          position: isMobile ? "fixed" : "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          width: "280px",
          zIndex: 1050,
          transform: isOpen
            ? "translateX(0)"
            : isMobile
            ? "translateX(-100%)"
            : "translateX(-100%)",
          transition: "transform 0.3s ease-in-out",
          boxShadow: isOpen ? "2px 0 8px rgba(0,0,0,0.15)" : "none",
          overflowY: "auto",
        }}
      >
        {/* Close button for mobile */}
        {isMobile && (
          <div className="d-flex justify-content-end p-3">
            <button
              onClick={onClose}
              className="btn btn-link text-white p-0"
              aria-label="Close sidebar"
              style={{ fontSize: "1.5rem" }}
            >
              <FaTimes />
            </button>
          </div>
        )}

        <Nav className="flex-column p-3 gap-2">
          {/* General Section */}
          <div className="text-muted small fw-bold text-uppercase px-3 mb-2 mt-2" style={{ letterSpacing: '1px', opacity: 0.6 }}>
            General
          </div>
          {links.slice(0, 5).map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }: { isActive: boolean }) =>
                `nav-link text-white d-flex align-items-center gap-2 rounded px-3 py-2 ${
                  isActive ? "bg-primary" : ""
                }`
              }
              onClick={handleLinkClick}
              style={{
                transition: "background-color 0.2s ease",
                textDecoration: "none",
              }}
            >
              {link.icon} {link.label}
            </NavLink>
          ))}

          {/* Catalog Section */}
          <div className="text-muted small fw-bold text-uppercase px-3 mb-2 mt-4" style={{ letterSpacing: '1px', opacity: 0.6 }}>
            Catalog & Design
          </div>
          {links.slice(5, 8).map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }: { isActive: boolean }) =>
                `nav-link text-white d-flex align-items-center gap-2 rounded px-3 py-2 ${
                  isActive ? "bg-primary" : ""
                }`
              }
              onClick={handleLinkClick}
              style={{
                transition: "background-color 0.2s ease",
                textDecoration: "none",
              }}
            >
              {link.icon} {link.label}
            </NavLink>
          ))}

          {/* Marketing & More Section */}
          <div className="text-muted small fw-bold text-uppercase px-3 mb-2 mt-4" style={{ letterSpacing: '1px', opacity: 0.6 }}>
            Marketing & Settings
          </div>
          {links.slice(8).map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }: { isActive: boolean }) =>
                `nav-link text-white d-flex align-items-center gap-2 rounded px-3 py-2 ${
                  isActive ? "bg-primary" : ""
                }`
              }
              onClick={handleLinkClick}
              style={{
                transition: "background-color 0.2s ease",
                textDecoration: "none",
              }}
            >
              {link.icon} {link.label}
            </NavLink>
          ))}
        </Nav>
      </div>
    </>
  );
}
